import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Copy,
  Check,
  TrendingUp,
  TrendingDown,
  ClipboardList,
  Target,
  History,
  ArrowRight,
  CircleSlash,
} from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { Button } from "../client/components/ui/button";
import { cn } from "../client/utils";
import type { EdgeReportContent } from "./reportSchema";

/**
 * Cascading reveal of the Edge Report. Every animation is transform/opacity only so
 * the sequence stays on the compositor at 60fps; under prefers-reduced-motion the
 * blocks simply appear.
 */

const STAGGER_MS = 150;

function useCopy() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard can be blocked (insecure context, permissions) — stay silent
      // rather than throwing an error at the user mid-reveal.
    }
  };

  return { copied, copy };
}

function CopyButton({ text }: { text: string }) {
  const t = useTranslation();
  const { copied, copy } = useCopy();

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-7 gap-1 px-2 text-xs text-muted-foreground"
      onClick={() => copy(text)}
      aria-label={copied ? t.edgy.reveal.copied : t.edgy.reveal.copy}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? t.edgy.reveal.copied : t.edgy.reveal.copy}
    </Button>
  );
}

function Block({
  index,
  title,
  icon,
  accent,
  copyText,
  children,
}: {
  index: number;
  title: string;
  icon: React.ReactNode;
  accent: string;
  copyText: string;
  children: React.ReactNode;
}) {
  const reduced = useReducedMotion() ?? false;

  return (
    <motion.section
      initial={reduced ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.45,
        delay: reduced ? 0 : (index * STAGGER_MS) / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="rounded-lg border border-border bg-card p-3"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className={cn("flex items-center gap-1.5 text-sm font-semibold", accent)}>
          {icon}
          {title}
        </h3>
        <CopyButton text={copyText} />
      </div>
      {children}
    </motion.section>
  );
}

/** Circular Edge Score gauge with a counting number. */
function EdgeScoreGauge({ score, delayMs }: { score: number; delayMs: number }) {
  const t = useTranslation();
  const reduced = useReducedMotion() ?? false;
  const [display, setDisplay] = useState(reduced ? score : 0);

  useEffect(() => {
    if (reduced) {
      setDisplay(score);
      return;
    }
    const timeout = setTimeout(() => {
      let frame: number;
      const start = performance.now();
      const duration = 1200;
      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(Math.round(score * eased));
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frame);
    }, delayMs);

    return () => clearTimeout(timeout);
  }, [score, delayMs, reduced]);

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - display / 100);

  const tone =
    score >= 70 ? "text-emerald-500" : score >= 45 ? "text-amber-500" : "text-red-500";

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: reduced ? 0 : delayMs / 1000, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card p-4"
    >
      <div className="relative h-32 w-32">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            strokeWidth="8"
            className="stroke-muted"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={cn("transition-[stroke-dashoffset] duration-300", tone)}
            stroke="currentColor"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("font-mono text-3xl font-bold tabular-nums", tone)}>{display}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t.edgy.reveal.edgeScore}
          </span>
        </div>
      </div>
      {/* Discreet celebration: a single pulse, no confetti library. */}
      {score > 70 && !reduced && (
        <motion.span
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: [0, 1, 0], scale: [0.8, 1.15, 1.3] }}
          transition={{ duration: 1.1, delay: delayMs / 1000 + 0.9 }}
          className="pointer-events-none absolute h-32 w-32 rounded-full border-2 border-emerald-400"
        />
      )}
    </motion.div>
  );
}

/** Plain-text rendering used by the copy buttons. */
function formatReport(report: EdgeReportContent, t: any): string {
  const lines: string[] = [];
  lines.push(`${t.edgy.reveal.edgeScore}: ${report.edgeScore}/100`);
  lines.push(report.summary);
  lines.push("");

  if (report.progress) {
    lines.push(`## ${t.edgy.progress.title}`);
    lines.push(`${t.edgy.reveal.edgeScore}: ${report.progress.scoreDelta > 0 ? "+" : ""}${report.progress.scoreDelta}`);
    report.progress.actionsFollowUp.forEach((a) => lines.push(`- ${a.action} — ${a.verdict}${a.stat ? ` (${a.stat})` : ""}`));
    lines.push("");
  }

  lines.push(`## ${t.edgy.reveal.strengths}`);
  report.strengths.forEach((s) => lines.push(`- ${s.title} — ${s.detail} (${s.stat})`));
  lines.push("");

  lines.push(`## ${t.edgy.reveal.leaks}`);
  report.leaks.forEach((l) =>
    lines.push(`- ${l.title} — ${l.detail} (${l.stat}) · ${t.edgy.reveal.estimatedCost}: ${l.estimatedCost}`)
  );

  if (report.playbookGaps.length > 0) {
    lines.push("");
    lines.push(`## ${t.edgy.reveal.playbookGaps}`);
    report.playbookGaps.forEach((g) =>
      lines.push(`- ${g.rule}: ${t.edgy.reveal.declared} ${g.declared} / ${t.edgy.reveal.actual} ${g.actual} — ${g.impact}`)
    );
  }

  lines.push("");
  lines.push(`## ${t.edgy.reveal.weeklyActions}`);
  report.weeklyActions.forEach((a) => lines.push(`- ${a.action} (${t.edgy.reveal.checkMetric}: ${a.checkMetric})`));

  return lines.join("\n");
}

/** "Since last time" — rendered first when the report has one. */
function ProgressBlock({ progress, index }: { progress: NonNullable<EdgeReportContent["progress"]>; index: number }) {
  const t = useTranslation();
  const reduced = useReducedMotion() ?? false;
  const up = progress.scoreDelta > 0;
  const flat = progress.scoreDelta === 0;

  const verdictLabel: Record<string, string> = {
    followed: t.edgy.progress.followed,
    partially_followed: t.edgy.progress.partiallyFollowed,
    not_followed: t.edgy.progress.notFollowed,
    unverifiable: t.edgy.progress.unverifiable,
  };

  const verdictTone: Record<string, string> = {
    followed: "text-emerald-500",
    partially_followed: "text-amber-500",
    not_followed: "text-red-500",
    unverifiable: "text-muted-foreground",
  };

  return (
    <motion.section
      initial={reduced ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: reduced ? 0 : (index * STAGGER_MS) / 1000, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-lg border border-primary/40 bg-primary/5 p-3"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-primary">
          <History className="h-4 w-4" />
          {t.edgy.progress.title}
        </h3>
        <span
          className={cn(
            "flex items-center gap-1 font-mono text-sm font-semibold",
            flat ? "text-muted-foreground" : up ? "text-emerald-500" : "text-red-500"
          )}
        >
          <ArrowRight className={cn("h-3.5 w-3.5", !flat && (up ? "-rotate-45" : "rotate-45"))} />
          {progress.scoreDelta > 0 ? "+" : ""}
          {progress.scoreDelta}
        </span>
      </div>

      <ul className="flex flex-col gap-2">
        {progress.actionsFollowUp.map((item, i) => (
          <li key={i} className="text-sm">
            <div className="flex items-start gap-2">
              {item.verdict === "unverifiable" ? (
                <CircleSlash className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : item.verdict === "followed" ? (
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
              ) : (
                <Target className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", verdictTone[item.verdict])} />
              )}
              <div className="min-w-0">
                <p>{item.action}</p>
                <p className={cn("text-xs", verdictTone[item.verdict])}>
                  {verdictLabel[item.verdict]}
                  {item.stat && <span className="ml-1 font-mono text-muted-foreground">{item.stat}</span>}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </motion.section>
  );
}

export default function EdgeReportView({ report }: { report: EdgeReportContent }) {
  const t = useTranslation();
  const reduced = useReducedMotion() ?? false;

  const hasGaps = report.playbookGaps.length > 0;
  const hasProgress = !!report.progress;
  const offset = hasProgress ? 1 : 0;
  // The score lands last, after every other block has staggered in.
  const scoreDelay = ((hasGaps ? 4 : 3) + offset) * STAGGER_MS + 250;

  return (
    <div className="flex flex-col gap-3">
      <motion.div
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-start justify-between gap-2"
      >
        <p className="text-sm text-muted-foreground">{report.summary}</p>
        <CopyButton text={formatReport(report, t)} />
      </motion.div>

      {report.progress && <ProgressBlock progress={report.progress} index={0} />}

      <Block
        index={offset}
        title={t.edgy.reveal.strengths}
        icon={<TrendingUp className="h-4 w-4" />}
        accent="text-emerald-500"
        copyText={report.strengths.map((s) => `${s.title} — ${s.detail} (${s.stat})`).join("\n")}
      >
        <ul className="flex flex-col gap-2">
          {report.strengths.map((s, i) => (
            <li key={i} className="text-sm">
              <span className="font-medium">{s.title}</span>
              <p className="text-muted-foreground">{s.detail}</p>
              {s.stat && <span className="font-mono text-xs text-emerald-500">{s.stat}</span>}
            </li>
          ))}
        </ul>
      </Block>

      <Block
        index={offset + 1}
        title={t.edgy.reveal.leaks}
        icon={<TrendingDown className="h-4 w-4" />}
        accent="text-red-500"
        copyText={report.leaks
          .map((l) => `${l.title} — ${l.detail} (${l.stat}) · ${l.estimatedCost}`)
          .join("\n")}
      >
        <ul className="flex flex-col gap-3">
          {report.leaks.map((l, i) => (
            <li key={i} className="text-sm">
              <span className="font-medium">{l.title}</span>
              <p className="text-muted-foreground">{l.detail}</p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-mono text-lg font-semibold text-red-500">{l.estimatedCost}</span>
                {l.stat && <span className="font-mono text-xs text-muted-foreground">{l.stat}</span>}
              </div>
            </li>
          ))}
        </ul>
      </Block>

      {hasGaps && (
        <Block
          index={offset + 2}
          title={t.edgy.reveal.playbookGaps}
          icon={<Target className="h-4 w-4" />}
          accent="text-amber-500"
          copyText={report.playbookGaps
            .map((g) => `${g.rule}: ${g.declared} / ${g.actual} — ${g.impact}`)
            .join("\n")}
        >
          <ul className="flex flex-col gap-2">
            {report.playbookGaps.map((g, i) => (
              <li key={i} className="rounded-md border border-border p-2 text-sm">
                <span className="font-medium">{g.rule}</span>
                <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">{t.edgy.reveal.declared}</span>
                    <p className="font-mono">{g.declared}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t.edgy.reveal.actual}</span>
                    <p className="font-mono text-amber-500">{g.actual}</p>
                  </div>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{g.impact}</p>
              </li>
            ))}
          </ul>
        </Block>
      )}

      <Block
        index={offset + (hasGaps ? 3 : 2)}
        title={t.edgy.reveal.weeklyActions}
        icon={<ClipboardList className="h-4 w-4" />}
        accent="text-primary"
        copyText={report.weeklyActions.map((a) => `- ${a.action} (${a.checkMetric})`).join("\n")}
      >
        <ul className="flex flex-col gap-2">
          {report.weeklyActions.map((a, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 h-4 w-4 shrink-0 rounded border border-border" aria-hidden />
              <div>
                <p>{a.action}</p>
                <span className="font-mono text-xs text-muted-foreground">{a.checkMetric}</span>
              </div>
            </li>
          ))}
        </ul>
      </Block>

      <EdgeScoreGauge score={report.edgeScore} delayMs={scoreDelay} />
    </div>
  );
}
